import React, { useState, useEffect } from 'react';
import { X, Plus, Calendar, User, AlertTriangle } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';


const CreateTaskModal = ({ isOpen, onClose, onTaskCreated }) => {
    const [formData, setFormData] = useState({
        taskText: '',
        priority: 'medium',
        assignee: '',
        dueDate: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    // Authentication is handled by ensureAuthenticated() function
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return unsubscribe;
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Use the same authentication pattern that works for transcriptions
            const { waitForAuthState } = await import('../services/firebase');
            await waitForAuthState();
            
            const userId = currentUser?.uid;
            if (!userId) {
                throw new Error('User must be authenticated');
            }
            
            // Force token refresh to ensure we have a valid token
            if (currentUser) {
                await currentUser.getIdToken(true);
            }
            
            const { httpsCallable } = await import('firebase/functions');
            const { getFunctions } = await import('firebase/functions');
            // Ensure we're using the same region as the main firebase.js
            const functions = getFunctions(undefined, 'us-central1');
            
            const addManualTask = httpsCallable(functions, 'addManualTask');
            
            const result = await addManualTask({
                taskText: formData.taskText,
                priority: formData.priority,
                assignee: formData.assignee || null,
                dueDate: formData.dueDate || null,
                userId: userId
            });

            if (result.data.success) {
                onTaskCreated(result.data.task);
                setFormData({
                    taskText: '',
                    priority: 'medium',
                    assignee: '',
                    dueDate: ''
                });
                onClose();
            }
        } catch (error) {
            console.error('Error creating task:', error);
            setError(error.message || 'Failed to create task');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Create New Task</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                            <span className="text-red-700 text-sm">{error}</span>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Task Description *
                        </label>
                        <textarea
                            value={formData.taskText}
                            onChange={(e) => handleInputChange('taskText', e.target.value)}
                            placeholder="Enter task description..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows={3}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Priority
                        </label>
                        <select
                            value={formData.priority}
                            onChange={(e) => handleInputChange('priority', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assignee
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={formData.assignee}
                                onChange={(e) => handleInputChange('assignee', e.target.value)}
                                placeholder="Enter assignee name..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Due Date
                        </label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !formData.taskText.trim()}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {isLoading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Task
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateTaskModal;
